import { PRIMARY_BG } from '../../../theme/brand';
const TYPE_META = {
  'binary-poll':  { label: 'Poll',    emoji: '📊', color: '#818cf8' },
  'text-poll':    { label: 'Poll',    emoji: '📊', color: '#818cf8' },
  'image-poll':   { label: 'Poll',    emoji: '🖼️', color: '#a78bfa' },
  'text-quiz':    { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
  'image-quiz':   { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
  'slider':       { label: 'Slider',  emoji: '🎚️', color: '#38bdf8' },
  'results':      { label: 'Results', emoji: '📈', color: '#34d399' },
};

export default function InteractiveLayerRenderer({ layer }) {
  const data = layer.data;
  const isDark = (data.theme || 'dark') === 'dark';
  const meta = TYPE_META[data.interactiveType] || { label: 'Interactive', emoji: '✨', color: '#818cf8' };

  const bg       = isDark ? 'rgba(8,8,20,0.92)'          : 'rgba(255,255,255,0.96)';
  const text     = isDark ? '#ffffff'                     : '#111827';
  const muted    = isDark ? 'rgba(255,255,255,0.60)'      : 'rgba(0,0,0,0.50)';
  const optBg    = isDark ? 'rgba(255,255,255,0.10)'      : 'rgba(0,0,0,0.06)';
  const optBorder= isDark ? 'rgba(255,255,255,0.22)'      : 'rgba(0,0,0,0.14)';
  const trackBg  = isDark ? 'rgba(255,255,255,0.18)'      : 'rgba(0,0,0,0.12)';

  /* ── Binary poll ── */
  function renderBinaryPoll() {
    const opts = data.options || [];
    const labels = [opts[0]?.text || 'Yes', opts[1]?.text || 'No'];
    const accents = [PRIMARY_BG, '#ec4899'];
    const icons = ['👍', '👎'];

    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{
            flex: 1, borderRadius: 12, padding: '13px 8px',
            background: accents[i],
            border: `2px solid ${accents[i]}`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 5,
            cursor: 'pointer',
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{icons[i]}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
              {labels[i]}
            </span>
          </div>
        ))}
      </div>
    );
  }

  /* ── Poll / Quiz options ── */
  function renderOptions() {
    const opts = (data.options || []).slice(0, 4);
    const isQuiz = data.interactiveType?.includes('quiz');
    const letters = ['A', 'B', 'C', 'D'];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {opts.map((o, i) => {
          const correct = 'isCorrect' in o ? o.isCorrect : false;
          return (
            <div key={o.id || i} style={{
              background: correct ? 'rgba(34,197,94,0.20)' : optBg,
              border: `1.5px solid ${correct ? '#4ade80' : optBorder}`,
              borderRadius: 10, padding: '9px 12px',
              display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
            }}>
              {isQuiz && (
                <span style={{
                  width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                  background: correct ? '#22c55e' : 'rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800,
                  color: '#ffffff',
                }}>
                  {letters[i]}
                </span>
              )}
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: text, lineHeight: 1.35 }}>
                {o.text || `Option ${i + 1}`}
              </span>
              {correct && <span style={{ fontSize: 13, color: '#4ade80' }}>✓</span>}
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Slider ── */
  function renderSlider() {
    const min = data.sliderMin ?? 0;
    const max = data.sliderMax ?? 100;
    const val = data.sliderInitialValue ?? 50;
    const pct = ((val - min) / (max - min)) * 100;

    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <span style={{
            background: meta.color, color: '#fff',
            fontSize: 18, fontWeight: 800, borderRadius: 8,
            padding: '3px 14px',
          }}>{val}</span>
        </div>
        <div style={{ position: 'relative', height: 8, background: trackBg, borderRadius: 4, margin: '0 12px' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: 4 }} />
          <div style={{
            position: 'absolute', top: '50%', left: `${pct}%`,
            transform: 'translate(-50%, -50%)',
            width: 22, height: 22, borderRadius: '50%',
            background: meta.color, border: '3px solid #fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, padding: '0 6px', fontSize: 11, color: muted }}>
          <span>{min}</span><span>{max}</span>
        </div>
      </div>
    );
  }

  /* ── Results ── */
  function renderResults() {
    const opts = (data.options || []).slice(0, 4);
    const votes = [42, 28, 18, 12];
    const total = 100;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {opts.map((o, i) => (
          <div key={o.id || i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: text }}>{o.text || `Option ${i + 1}`}</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: meta.color }}>{votes[i]}%</span>
            </div>
            <div style={{ height: 6, background: trackBg, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${votes[i]}%`, height: '100%', background: meta.color, borderRadius: 3 }} />
            </div>
          </div>
        ))}
        <p style={{ fontSize: 10, color: muted, textAlign: 'center', marginTop: 2 }}>{total} responses</p>
      </div>
    );
  }

  function renderContent() {
    if (data.interactiveType === 'binary-poll') return renderBinaryPoll();
    if (data.interactiveType === 'slider')       return renderSlider();
    if (data.interactiveType === 'results')      return renderResults();
    return renderOptions();
  }

  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      background: bg,
      borderRadius: 16, padding: '13px 12px',
      display: 'flex', flexDirection: 'column', gap: 10,
      border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)'}`,
      backdropFilter: 'blur(20px)',
      overflow: 'hidden',
    }}>
      {/* Badge row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>{meta.emoji}</span>
        <span style={{
          background: meta.color, color: '#ffffff',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
          textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20,
        }}>
          {meta.label}
        </span>
      </div>

      {/* Prompt */}
      <p style={{
        margin: 0, fontSize: 15, fontWeight: 700,
        color: text, lineHeight: 1.4, letterSpacing: '-0.01em',
      }}>
        {data.prompt || <span style={{ color: muted, fontStyle: 'italic', fontWeight: 400 }}>Add a prompt…</span>}
      </p>

      {renderContent()}

      {data.showConfetti && (
        <p style={{ margin: 0, fontSize: 10, color: muted, textAlign: 'center' }}>🎉 Confetti on completion</p>
      )}
    </div>
  );
}
